import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurrentUserBranch } from '../../../services/authService';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
  Switch,
  ListItemText,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  ViewList as ViewListIcon,
  List as ListIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import apiService from '../../../services/apiService';

// إضافة خاصية اختيارية لمعرف MongoDB
type MongoId = {
  _id?: string;
};

// واجهة الحساب - تحسين للتوافق مع MongoDB و TypeScript
interface Account extends MongoId {
  id: string;
  accountNumber: string;
  name: string;
  balance: number;
  type: string;
}

// واجهة مجموعة الرسوم - تحسين للتوافق مع MongoDB و TypeScript
interface FeeGroup extends MongoId {
  id: string;
  name: string;
  description?: string;
}

// نوع مجموعة الرسوم يمكن أن يكون معرف (سلسلة نصية) أو كائن مجموعة الرسوم
type FeeGroupType = string | FeeGroup;

// واجهة قالب السند المتخصص - تحسين للتوافق مع MongoDB و TypeScript
interface SpecializedVoucherTemplate extends MongoId {
  id?: string;
  name: string;
  voucherType: 'receipt' | 'payment' | 'journal';
  description?: string;
  includeVoucherNumber: boolean;
  includeDate: boolean;
  includeAmount: boolean;
  includeOperation: boolean;
  includeNotes: boolean;
  debitAccounts: string[];  // معرفات الحسابات المدينة
  creditAccounts: string[];  // معرفات الحسابات الدائنة
  allowMultipleDebitAccounts: boolean;
  allowMultipleCreditAccounts: boolean;
  requiresApproval: boolean; // تم تغييرها من requireApproval لتتوافق مع الخادم
  hasFees: boolean;        // تم تغييرها من applyFees لتتوافق مع الخادم
  feeGroups: string[];     // معرفات مجموعات الرسوم
  branch?: string;         // معرف الفرع
}

// واجهة بيانات المستخدم
interface UserData {
  id: string;
  name: string;
  branch: string;
  role: string;
}

// استخراج المعرف من كائن MongoDB - وظيفة مساعدة
const getIdFromMongoObject = (obj: any): string => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj.id || obj._id || '';
};

const SpecializedVoucherTemplateForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [feeGroups, setFeeGroups] = useState<FeeGroup[]>([]);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  const [feeGroupsLoading, setFeeGroupsLoading] = useState<boolean>(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [feeGroupsError, setFeeGroupsError] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [templateData, setTemplateData] = useState<SpecializedVoucherTemplate | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  
  // إعداد بيانات مستخدم افتراضية مع معرف الفرع
  const setDefaultUserData = async (branchId: string) => {
    const defaultUserData: UserData = {
      id: 'default-user',
      name: 'مستخدم النظام',
      branch: branchId,
      role: 'admin'
    };
    
    // تعيين بيانات المستخدم في الحالة
    setUserData(defaultUserData);
    
    // حفظ بيانات المستخدم الافتراضية في التخزين المحلي
    try {
      localStorage.setItem('userData', JSON.stringify(defaultUserData));
      console.log('تم حفظ بيانات المستخدم الافتراضية في التخزين المحلي');
    } catch (error) {
      console.error('فشل في حفظ بيانات المستخدم في التخزين المحلي:', error);
    }
  };
  
  // تحديد القيم الأولية للنموذج
  const getInitialValues = (): SpecializedVoucherTemplate => {
    if (isEditMode && templateData) {
      // إذا كنا في وضع التعديل وتم تحميل بيانات القالب، استخدم هذه البيانات
      return {
        name: templateData.name || '',
        voucherType: templateData.voucherType || 'receipt',
        description: templateData.description || '',
        includeVoucherNumber: templateData.includeVoucherNumber === undefined ? true : Boolean(templateData.includeVoucherNumber),
        includeDate: templateData.includeDate === undefined ? true : Boolean(templateData.includeDate),
        includeAmount: templateData.includeAmount === undefined ? true : Boolean(templateData.includeAmount),
        includeOperation: templateData.includeOperation === undefined ? false : Boolean(templateData.includeOperation),
        includeNotes: templateData.includeNotes === undefined ? true : Boolean(templateData.includeNotes),
        debitAccounts: Array.isArray(templateData.debitAccounts) ? templateData.debitAccounts : [],
        creditAccounts: Array.isArray(templateData.creditAccounts) ? templateData.creditAccounts : [],
        allowMultipleDebitAccounts: templateData.allowMultipleDebitAccounts === undefined ? false : Boolean(templateData.allowMultipleDebitAccounts),
        allowMultipleCreditAccounts: templateData.allowMultipleCreditAccounts === undefined ? false : Boolean(templateData.allowMultipleCreditAccounts),
        requiresApproval: templateData.requiresApproval === undefined ? false : Boolean(templateData.requiresApproval),
        hasFees: templateData.hasFees === undefined ? false : Boolean(templateData.hasFees),
        feeGroups: Array.isArray(templateData.feeGroups) ? templateData.feeGroups : [],
        branch: templateData.branch || userData?.branch || ''
      };
    } else {
      // إذا كنا في وضع الإنشاء أو لم يتم تحميل البيانات بعد، استخدم القيم الافتراضية
      return {
        name: '',
        voucherType: 'receipt',
        description: '',
        includeVoucherNumber: true,
        includeDate: true,
        includeAmount: true,
        includeOperation: false,
        includeNotes: true,
        debitAccounts: [],
        creditAccounts: [],
        allowMultipleDebitAccounts: false,
        allowMultipleCreditAccounts: false,
        requiresApproval: false,
        hasFees: false,
        feeGroups: [],
        branch: userData?.branch || ''
      };
    }
  };

  // نموذج إنشاء/تعديل قالب السند المتخصص
  const formik = useFormik<SpecializedVoucherTemplate>({
    initialValues: getInitialValues(),
    enableReinitialize: false, // تعطيل إعادة تهيئة النموذج تلقائيًا لتجنب فقدان البيانات
    validationSchema: Yup.object({
      name: Yup.string().required('اسم القالب مطلوب'),
      voucherType: Yup.string()
        .oneOf(['receipt', 'payment', 'journal'], 'نوع السند غير صالح')
        .required('نوع السند مطلوب'),
      debitAccounts: Yup.array()
        .min(1, 'يجب اختيار حساب مدين واحد على الأقل')
        .required('الحسابات المدينة مطلوبة'),
      creditAccounts: Yup.array()
        .min(1, 'يجب اختيار حساب دائن واحد على الأقل')
        .required('الحسابات الدائنة مطلوبة'),
      branch: Yup.string().required('معرف الفرع مطلوب'),
      feeGroups: Yup.array()
        .test('required-if-fees-enabled', 'يجب اختيار مجموعة رسوم واحدة على الأقل عند تفعيل الرسوم', function(value) {
          return !this.parent.hasFees || (value && value.length > 0);
        })
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      
      // استخدام معرف الفرع الموجود في النموذج (من localStorage أو بيانات المستخدم)
      try {
        // استخدام القيمة الموجودة في النموذج بدلاً من طلبها مرة أخرى
        let branchId = values.branch;
        
        // إذا لم تكن موجودة في النموذج، حاول الحصول عليها من التخزين المحلي
        if (!branchId) {
          branchId = localStorage.getItem('activeBranchId') || '';
        }
        
        // فقط إذا لم تكن موجودة في النموذج ولا في التخزين المحلي، قم بطلبها من الخادم
        if (!branchId) {
          console.warn('معرف الفرع غير موجود في النموذج أو التخزين المحلي - جلب من الخادم');
          const fetchedBranchId = await getCurrentUserBranch();
          if (fetchedBranchId) {
            branchId = fetchedBranchId;
            console.log('تم استرجاع معرف الفرع من الخادم:', branchId);
          }
        }
        
        if (!branchId) {
          console.error('لم يتم العثور على معرف الفرع!');
          setError('لم يتم العثور على معرف الفرع. يرجى إعادة تسجيل الدخول وتحديث الصفحة.');
          setLoading(false);
          return;
        }
        
        // تأكد من تضمين معرف الفرع في القيم - هذا ضروري لنجاح العملية
        values.branch = branchId;
        
        // تبسيط معالجة مجموعات الرسوم
        // إذا كان hasFees = false، نرسل مصفوفة فارغة
        // وإلا نتأكد من أن جميع العناصر هي معرفات نصية
        
        let feeGroupIds: string[] = [];
        
        if (values.hasFees && values.feeGroups.length > 0) {
          feeGroupIds = values.feeGroups.map(group => {
            // تحويل كل عنصر إلى معرف نصي
            return typeof group === 'string' ? group : (group as FeeGroup).id;
          }).filter(Boolean); // استبعاد القيم الفارغة
        }
        
        const cleanedValues = {
          ...values,
          feeGroups: feeGroupIds
        };
        
        // طباعة البيانات المرسلة للتأكد من تضمين معرف الفرع ومجموعات الرسوم
        console.log('البيانات المرسلة للخادم:', cleanedValues);
        
        if (isEditMode && id) {
          // تحديث قالب موجود
          await apiService.put(`/finance/specialized-vouchers/templates/${id}`, cleanedValues);
        } else {
          // إنشاء قالب جديد
          await apiService.post('/finance/specialized-vouchers/templates', cleanedValues);
        }
        
        // إعادة تعيين حالة التحميل قبل الانتقال
        setLoading(false);
        
        // العودة إلى قائمة القوالب مع رسالة نجاح
        navigate('/finance/specialized-vouchers?tab=templates&success=true');
      } catch (error: any) {
        // التحقق من نوع الخطأ
        if (error.message && error.message.includes('localStorage')) {
          console.error('خطأ في الوصول إلى التخزين المحلي:', error);
          setError('حدث خطأ في الوصول إلى بيانات المستخدم. يرجى التأكد من تمكين التخزين المحلي في المتصفح.');
        } else {
          console.error('خطأ في حفظ قالب السند المتخصص:', error);
          setError(error.response?.data?.message || 'حدث خطأ في حفظ قالب السند المتخصص');
        }
        setLoading(false);
      }
    }
  });
  
  // الحصول على بيانات المستخدم من التخزين المحلي - يتم تنفيذه مرة واحدة فقط عند تحميل المكون
  useEffect(() => {
    // استخدام متغير لتتبع ما إذا كان المكون لا يزال مثبتًا
    let isComponentMounted = true;
    let branchIdFromStorage = '';
    
    const initializeUserData = async () => {
      try {
        // 1. محاولة الحصول على معرف الفرع من التخزين المحلي
        const cachedBranchId = localStorage.getItem('activeBranchId');
        if (cachedBranchId) {
          branchIdFromStorage = cachedBranchId;
          console.log('تم العثور على معرف الفرع في التخزين المحلي:', cachedBranchId);
        }
        
        // 2. محاولة الحصول على بيانات المستخدم من التخزين المحلي
        const userDataStr = localStorage.getItem('userData');
        let parsedUserData: UserData | null = null;
        
        if (userDataStr) {
          try {
            parsedUserData = JSON.parse(userDataStr);
            if (isComponentMounted) {
              setUserData(parsedUserData);
              console.log('تم تحميل بيانات المستخدم من التخزين المحلي:', parsedUserData);
              
              // إذا كان هناك معرف فرع في بيانات المستخدم، استخدمه
              if (parsedUserData && parsedUserData.branch) {
                branchIdFromStorage = parsedUserData.branch;
              }
            }
          } catch (e) {
            console.error('خطأ في تحليل بيانات المستخدم:', e);
          }
        }
        
        // 3. إذا لم نجد معرف الفرع في التخزين المحلي، نحاول الحصول عليه من الخادم
        if (!branchIdFromStorage) {
          const branchId = await getCurrentUserBranch();
          if (branchId && isComponentMounted) {
            branchIdFromStorage = branchId;
            console.log('تم الحصول على معرف الفرع من الخادم:', branchId);
            
            // إنشاء بيانات مستخدم افتراضية إذا لم تكن موجودة
            if (!parsedUserData) {
              await setDefaultUserData(branchId);
            }
          }
        }
        
        // 4. إذا كنا في وضع الإنشاء (وليس التعديل)، نقوم بتعيين معرف الفرع في النموذج
        if (!isEditMode && branchIdFromStorage && isComponentMounted) {
          formik.setFieldValue('branch', branchIdFromStorage);
        }
        
      } catch (error) {
        console.error('خطأ في تهيئة بيانات المستخدم:', error);
      }
    };
    
    initializeUserData();
    
    // تنظيف عند إزالة المكون
    return () => {
      isComponentMounted = false;
    };
  }, []);
  
  // جلب بيانات القالب في حالة التعديل - يتم فصله عن تحديث النموذج
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!isEditMode || !id || isDataLoaded) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // جلب بيانات القالب من الخادم
        console.log(`جلب بيانات القالب بالمعرف: ${id}`);
        const response = await apiService.get<any>(`/finance/specialized-vouchers/templates/${id}`);
        
        if (!response) {
          throw new Error('لم يتم العثور على بيانات القالب');
        }
        
        console.log('بيانات القالب المستلمة من الخادم:', response);
        
        // معالجة خاصة للحسابات المدينة والدائنة ومجموعات الرسوم
        // تأكد من أن البيانات المستلمة تحتوي على مصفوفات فارغة بدلاً من undefined
        const debitAccountIds = Array.isArray(response.debitAccounts) ? 
          response.debitAccounts.map((acc: any) => getIdFromMongoObject(acc)) : [];
        
        const creditAccountIds = Array.isArray(response.creditAccounts) ? 
          response.creditAccounts.map((acc: any) => getIdFromMongoObject(acc)) : [];
        
        const feeGroupIds = Array.isArray(response.feeGroups) ? 
          response.feeGroups.map((group: any) => getIdFromMongoObject(group)) : [];
        
        // تجهيز البيانات المعالجة
        const normalizedTemplate: SpecializedVoucherTemplate = {
          id: response.id || response._id,
          _id: response._id || response.id,
          name: response.name || '',
          voucherType: response.voucherType || 'receipt',
          description: response.description || '',
          includeVoucherNumber: response.includeVoucherNumber === undefined ? true : Boolean(response.includeVoucherNumber),
          includeDate: response.includeDate === undefined ? true : Boolean(response.includeDate),
          includeAmount: response.includeAmount === undefined ? true : Boolean(response.includeAmount),
          includeOperation: response.includeOperation === undefined ? false : Boolean(response.includeOperation),
          includeNotes: response.includeNotes === undefined ? true : Boolean(response.includeNotes),
          debitAccounts: debitAccountIds,
          creditAccounts: creditAccountIds,
          allowMultipleDebitAccounts: response.allowMultipleDebitAccounts === undefined ? false : Boolean(response.allowMultipleDebitAccounts),
          allowMultipleCreditAccounts: response.allowMultipleCreditAccounts === undefined ? false : Boolean(response.allowMultipleCreditAccounts),
          requiresApproval: response.requiresApproval === undefined ? false : Boolean(response.requiresApproval),
          hasFees: response.hasFees === undefined ? false : Boolean(response.hasFees),
          feeGroups: feeGroupIds,
          branch: response.branch || localStorage.getItem('activeBranchId') || ''
        };
        
        // طباعة بيانات الحسابات بعد المعالجة للتصحيح
        console.log('الحسابات المدينة بعد المعالجة:', normalizedTemplate.debitAccounts);
        console.log('الحسابات الدائنة بعد المعالجة:', normalizedTemplate.creditAccounts);
        console.log('مجموعات الرسوم بعد المعالجة:', normalizedTemplate.feeGroups);
        
        // تخزين البيانات المعالجة في الحالة
        setTemplateData(normalizedTemplate);
        setIsDataLoaded(true);
        
        // تعيين القيم في النموذج مباشرة بدلاً من الاعتماد على enableReinitialize
        formik.setValues(normalizedTemplate);
        
        console.log('تم تحميل وتعيين بيانات القالب بنجاح:', normalizedTemplate);
        
        setLoading(false);
      } catch (error: any) {
        console.error('خطأ في جلب بيانات القالب:', error);
        setError(error.response?.data?.message || 'حدث خطأ في جلب بيانات القالب');
        setLoading(false);
      }
    };
    
    fetchTemplate();
  }, [id, isEditMode, isDataLoaded]);
  
  // جلب الحسابات ومجموعات الرسوم
  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      setAccountsError(null);
      
      try {
        const response = await apiService.get<any[]>('/finance/accounts');
        
        // معالجة استجابة الحسابات لضمان توحيد تنسيق المعرفات
        const normalizedAccounts = response.map(account => ({
          ...account,
          // ضمان وجود حقل id
          id: account.id || account._id || '',
          _id: account._id || account.id || ''
        }));
        
        console.log('تم استرجاع الحسابات:', normalizedAccounts);
        console.log('عدد الحسابات المسترجعة:', normalizedAccounts.length);
        
        setAccounts(normalizedAccounts as Account[]);
        setAccountsLoading(false);
      } catch (error: any) {
        console.error('خطأ في جلب الحسابات:', error);
        setAccountsError(error.response?.data?.message || 'حدث خطأ في جلب الحسابات');
        setAccountsLoading(false);
      }
    };
    
    const fetchFeeGroups = async () => {
      setFeeGroupsLoading(true);
      setFeeGroupsError(null);
      
      try {
        const { apiWithContext } = await import('../../../services/api');
        const response = await apiWithContext.get('/finance/fee-groups', 'specialized-voucher').then(res => res.data);
        
        // تحويل البيانات لتتوافق مع واجهة FeeGroup مع ضمان توحيد تنسيق المعرفات
        const normalizedFeeGroups = response.map(group => ({
          ...group,
          id: group.id || group._id || '', // ضمان وجود حقل id
          _id: group._id || group.id || '', // ضمان وجود حقل _id
          name: group.name || '',
          description: group.description || ''
        }));
        
        // تأكد من عدم تكرار مجموعات الرسوم عن طريق إزالة العناصر المكررة
        const uniqueFeeGroups = normalizedFeeGroups.filter((group, index, self) =>
          index === self.findIndex((g) => (g.id === group.id) || (g._id === group._id))
        );
        
        console.log('تم استرجاع مجموعات الرسوم:', uniqueFeeGroups);
        console.log('عدد مجموعات الرسوم المسترجعة:', uniqueFeeGroups.length);
        setFeeGroups(uniqueFeeGroups as FeeGroup[]);
        setFeeGroupsLoading(false);
      } catch (error: any) {
        console.error('خطأ في جلب مجموعات الرسوم:', error);
        setFeeGroupsError(error.response?.data?.message || 'حدث خطأ في جلب مجموعات الرسوم');
        setFeeGroupsLoading(false);
      }
    };
    
    fetchAccounts();
    fetchFeeGroups();
  }, []);
  
  // الحصول على اسم الحساب من المعرف - تحسين المقارنة لتعمل مع _id و id
  const getAccountById = (accountId: string) => {
    const account = accounts.find(acc => acc.id === accountId || acc._id === accountId);
    return account ? `${account.name} (${account.accountNumber})` : accountId;
  };
  
  // الحصول على اسم مجموعة الرسوم من المعرف - تحسين المقارنة لتعمل مع _id و id
  const getFeeGroupById = (feeGroupId: string) => {
    const feeGroup = feeGroups.find(group => group.id === feeGroupId || group._id === feeGroupId);
    return feeGroup ? feeGroup.name : feeGroupId;
  };
  
  // عرض نص نوع السند بالعربية
  const getVoucherTypeText = (type: 'receipt' | 'payment' | 'journal') => {
    switch (type) {
      case 'receipt': return 'سند قبض';
      case 'payment': return 'سند صرف';
      case 'journal': return 'سند موازنة';
      default: return '';
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {isEditMode ? 'تعديل قالب سند متخصص' : 'إنشاء قالب سند متخصص جديد'}
          </Typography>
          
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/finance/specialized-vouchers?tab=templates')}
          >
            العودة للقائمة
          </Button>
        </Box>
        
        {/* عرض الأخطاء */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* حالة التحميل */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              {/* معلومات القالب الأساسية */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      معلومات القالب الأساسية
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          id="name"
                          name="name"
                          label="اسم القالب"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.name && Boolean(formik.errors.name)}
                          helperText={formik.touched.name && formik.errors.name}
                          required
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <FormControl 
                          fullWidth
                          error={formik.touched.voucherType && Boolean(formik.errors.voucherType)}
                          required
                        >
                          <InputLabel id="voucher-type-label">نوع السند</InputLabel>
                          <Select
                            labelId="voucher-type-label"
                            id="voucherType"
                            name="voucherType"
                            value={formik.values.voucherType}
                            onChange={formik.handleChange}
                            label="نوع السند"
                          >
                            <MenuItem value="receipt">سند قبض</MenuItem>
                            <MenuItem value="payment">سند صرف</MenuItem>
                            <MenuItem value="journal">سند موازنة</MenuItem>
                          </Select>
                          {formik.touched.voucherType && formik.errors.voucherType && (
                            <FormHelperText>{formik.errors.voucherType}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          id="description"
                          name="description"
                          label="وصف القالب"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* حقول السند */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      حقول السند
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      حدد الحقول التي ترغب في ظهورها في السند:
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formik.values.includeVoucherNumber)}
                              onChange={formik.handleChange}
                              name="includeVoucherNumber"
                              color="primary"
                            />
                          }
                          label="رقم السند (تلقائي)"
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formik.values.includeDate)}
                              onChange={formik.handleChange}
                              name="includeDate"
                              color="primary"
                            />
                          }
                          label="تاريخ السند"
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formik.values.includeAmount)}
                              onChange={formik.handleChange}
                              name="includeAmount"
                              color="primary"
                            />
                          }
                          label="المبلغ"
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formik.values.includeOperation)}
                              onChange={formik.handleChange}
                              name="includeOperation"
                              color="primary"
                            />
                          }
                          label="رقم العملية"
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(formik.values.includeNotes)}
                              onChange={formik.handleChange}
                              name="includeNotes"
                              color="primary"
                            />
                          }
                          label="الملاحظات"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* الحسابات */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      إعدادات الحسابات
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {accountsError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {accountsError}
                      </Alert>
                    )}
                    
                    {accountsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : (
                      <Grid container spacing={3}>
                        {/* الحسابات المدينة */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccountBalanceIcon fontSize="small" sx={{ mr: 1 }} />
                                الحسابات المدينة
                              </Box>
                            </Typography>
                            
                            <FormControl 
                              fullWidth
                              error={formik.touched.debitAccounts && Boolean(formik.errors.debitAccounts)}
                              sx={{ mb: 2 }}
                            >
                              <Autocomplete
                                multiple
                                id="debitAccounts"
                                options={accounts.map(account => account.id || account._id || '')}
                                getOptionLabel={(option) => getAccountById(option)}
                                value={formik.values.debitAccounts || []}
                                onChange={(event, newValue) => {
                                  console.log('تحديث الحسابات المدينة:', newValue);
                                  formik.setFieldValue('debitAccounts', newValue);
                                }}
                                isOptionEqualToValue={(option, value) => {
                                  // تحسين المقارنة للعمل مع الكائنات و السلاسل النصية
                                  if (typeof value === 'object' && value !== null) {
                                    const valueId = (value as any).id || (value as any)._id;
                                    return option === valueId;
                                  }
                                  return option === value;
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="الحسابات المدينة"
                                    placeholder="اختر الحسابات المدينة"
                                    error={formik.touched.debitAccounts && Boolean(formik.errors.debitAccounts)}
                                    helperText={formik.touched.debitAccounts && formik.errors.debitAccounts}
                                  />
                                )}
                                renderTags={(value, getTagProps) =>
                                  value.map((option, index) => (
                                    <Chip
                                      label={getAccountById(option)}
                                      {...getTagProps({ index })}
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ))
                                }
                              />
                            </FormControl>
                            
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(formik.values.allowMultipleDebitAccounts)}
                                  onChange={formik.handleChange}
                                  name="allowMultipleDebitAccounts"
                                  color="primary"
                                />
                              }
                              label="السماح باختيار أكثر من حساب مدين في السند"
                            />
                          </Box>
                        </Grid>
                        
                        {/* الحسابات الدائنة */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccountBalanceIcon fontSize="small" sx={{ mr: 1 }} />
                                الحسابات الدائنة
                              </Box>
                            </Typography>
                            
                            <FormControl 
                              fullWidth
                              error={formik.touched.creditAccounts && Boolean(formik.errors.creditAccounts)}
                              sx={{ mb: 2 }}
                            >
                              <Autocomplete
                                multiple
                                id="creditAccounts"
                                options={accounts.map(account => account.id || account._id || '')}
                                getOptionLabel={(option) => getAccountById(option)}
                                value={formik.values.creditAccounts || []}
                                onChange={(event, newValue) => {
                                  console.log('تحديث الحسابات الدائنة:', newValue);
                                  formik.setFieldValue('creditAccounts', newValue);
                                }}
                                isOptionEqualToValue={(option, value) => {
                                  // تحسين المقارنة للعمل مع الكائنات و السلاسل النصية
                                  if (typeof value === 'object' && value !== null) {
                                    const valueId = (value as any).id || (value as any)._id;
                                    return option === valueId;
                                  }
                                  return option === value;
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="الحسابات الدائنة"
                                    placeholder="اختر الحسابات الدائنة"
                                    error={formik.touched.creditAccounts && Boolean(formik.errors.creditAccounts)}
                                    helperText={formik.touched.creditAccounts && formik.errors.creditAccounts}
                                  />
                                )}
                                renderTags={(value, getTagProps) =>
                                  value.map((option, index) => (
                                    <Chip
                                      label={getAccountById(option)}
                                      {...getTagProps({ index })}
                                      color="secondary"
                                      variant="outlined"
                                    />
                                  ))
                                }
                              />
                            </FormControl>
                            
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={Boolean(formik.values.allowMultipleCreditAccounts)}
                                  onChange={formik.handleChange}
                                  name="allowMultipleCreditAccounts"
                                  color="primary"
                                />
                              }
                              label="السماح باختيار أكثر من حساب دائن في السند"
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* إعدادات إضافية */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      إعدادات إضافية
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(formik.values.requiresApproval)}
                              onChange={formik.handleChange}
                              name="requiresApproval"
                              color="primary"
                            />
                          }
                          label="يحتاج السند للموافقة بعد الحفظ"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(formik.values.hasFees)}
                              onChange={formik.handleChange}
                              name="hasFees"
                              color="primary"
                            />
                          }
                          label="تطبيق رسوم على السند"
                        />
                      </Grid>
                      
                      {formik.values.hasFees && (
                        <Grid item xs={12}>
                          <Box sx={{ ml: 4, mt: 1 }}>
                            {feeGroupsError && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {feeGroupsError}
                              </Alert>
                            )}
                            
                            {feeGroupsLoading ? (
                              <Box sx={{ display: 'flex', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : (
                              <>
                                <FormControl 
                                  fullWidth
                                  error={formik.touched.feeGroups && Boolean(formik.errors.feeGroups)}
                                >
                                  <Autocomplete
                                    multiple
                                    id="feeGroups"
                                    options={feeGroups}
                                    getOptionLabel={(option) => {
                                      // تحسين استخراج الاسم من الخيار
                                      if (typeof option === 'string') {
                                        return getFeeGroupById(option) || option;
                                      }
                                      return option.name || '';
                                    }}
                                    value={formik.values.feeGroups.map(id => {
                                      // البحث عن كائن مجموعة الرسوم المطابق للمعرف - تحسين المقارنة
                                      return feeGroups.find(group => group.id === id || group._id === id) || id;
                                    })}
                                    onChange={(event, newValue) => {
                                      // تحسين تحويل القيم إلى معرفات
                                      const feeGroupIds = newValue.map(item => {
                                        if (typeof item === 'string') {
                                          return item;
                                        } else {
                                          return item.id || (item as any)._id;
                                        }
                                      });
                                      console.log('تحديث مجموعات الرسوم:', feeGroupIds);
                                      formik.setFieldValue('feeGroups', feeGroupIds);
                                    }}
                                    isOptionEqualToValue={(option, value) => {
                                      // تحسين المقارنة بين الخيارات
                                      if (typeof option === 'string' && typeof value === 'string') {
                                        return option === value;
                                      }
                                      
                                      const optionId = typeof option === 'string' ? option : (option.id || (option as any)._id);
                                      
                                      if (typeof value === 'string') {
                                        return optionId === value;
                                      } else if (value && typeof value === 'object') {
                                        const valueId = value.id || (value as any)._id;
                                        return optionId === valueId;
                                      }
                                      
                                      return false;
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="مجموعات الرسوم"
                                        placeholder="اختر مجموعات الرسوم"
                                        error={formik.touched.feeGroups && Boolean(formik.errors.feeGroups)}
                                        helperText={formik.touched.feeGroups && formik.errors.feeGroups}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value.map((option, index) => (
                                        <Chip
                                          label={typeof option === 'string' ? getFeeGroupById(option) || option : option.name}
                                          {...getTagProps({ index })}
                                          color="default"
                                          variant="outlined"
                                        />
                                      ))
                                    }
                                  />
                                </FormControl>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  يمكن اختيار أكثر من مجموعة رسوم، ولكن سيتم تطبيق مجموعة واحدة فقط عند إصدار السند.
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                  <Alert severity="info" icon={<InfoIcon />}>
                                    يتم تطبيق الرسوم على الحساب المدين فقط، ولا تؤثر على الحساب الدائن.
                                  </Alert>
                                </Box>
                                
                                {feeGroups.length === 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      startIcon={<AddIcon />}
                                      onClick={() => navigate('/finance/fee-groups/create')}
                                      size="small"
                                    >
                                      إنشاء مجموعة رسوم جديدة
                                    </Button>
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                    
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" icon={<InfoIcon />}>
                        عند إصدار سند من هذا القالب، سيتم التأثير على أرصدة الحسابات العامة وتسجيل المعاملة فيها.
                      </Alert>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* أزرار الحفظ والإلغاء */}
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate('/finance/specialized-vouchers?tab=templates')}
                  disabled={loading}
                >
                  إلغاء
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                >
                  {isEditMode ? 'حفظ التعديلات' : 'إنشاء القالب'}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default SpecializedVoucherTemplateForm;